// SB6-ANA-POP-01 (Q3) — browser-capability advice for the Viewer pop-out.
//
// Christian: "What happens if the user is using Mac? There is no Window
// Management API … Safari is slow … we need to show a Popup to inform the
// user that another browser like Chrome will be faster."
//
// On a browser WITHOUT the Window Management API (Safari / macOS, Firefox)
// the pop-out still works, but it (a) can't be placed full-size on the
// second monitor and (b) syncs over the slower localStorage-event polyfill.
// We surface a single, once-per-session snackbar recommending a Chromium
// browser. Gated on the *capability* (Window Management support), not the
// user-agent string, so it's robust to UA spoofing and future engines that
// add support.
//
// The session gate uses `sessionStorage` so the advice shows once on a
// fresh session and stays quiet for subsequent pop-outs — same pattern as
// `safariDegradedMode`, which this generalises.

const SESSION_FLAG = "aero.lyse.popoutBrowserAdviceShown";

export const POPOUT_BROWSER_ADVICE_COPY =
  "For the best pop-out experience, use Chrome or Edge: they can place the " +
  "Viewer full-size on your second monitor and keep both windows in sync " +
  "faster. The Viewer still works here in its own window.";

/**
 * Should we show the browser-advice snackbar?
 *  - only when the Window Management API is unavailable (`supported=false`),
 *  - and not already shown this session.
 */
export function shouldShowBrowserAdvice(args: {
  /** Pass `hasWindowManagement()` here. */
  readonly supported: boolean;
}): boolean {
  if (args.supported) return false;
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(SESSION_FLAG) === null;
  } catch {
    return false;
  }
}

export function markBrowserAdviceShown(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(SESSION_FLAG, "1");
  } catch {
    /* swallow — session-storage may be disabled */
  }
}

/** Test helper — re-enables the advice. */
export function resetBrowserAdviceFlag(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(SESSION_FLAG);
  } catch {
    /* swallow */
  }
}
